import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { BankInfoFormData } from '@/types'
import { settingsService } from '@/services/settingsService'

const BankSettingsTab: React.FC = () => {
  const { user, updateUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  
  const [bankData, setBankData] = useState<BankInfoFormData>({
    bankName: '',
    bankCode: '',
    branchName: '',
    branchCode: '',
    accountNumber: '',
    accountHolder: ''
  })

  // 初始化表單數據
  useEffect(() => {
    if (user && user.bankInfo) {
      setBankData({
        bankName: user.bankInfo.bankName,
        bankCode: (user.bankInfo as any).bankCode || '',
        branchName: user.bankInfo.branchName,
        branchCode: user.bankInfo.branchCode || '',
        accountNumber: user.bankInfo.accountNumber,
        accountHolder: user.bankInfo.accountHolder
      })
    }
  }, [user])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setIsLoading(true)
    try {
      // 更新銀行資料
      const updatedUser = await settingsService.updateBankInfo({
        bankName: bankData.bankName,
        bankCode: bankData.bankCode,
        branchName: bankData.branchName,
        branchCode: bankData.branchCode,
        accountNumber: bankData.accountNumber,
        accountHolder: bankData.accountHolder
      })
      
      // 更新本地用戶狀態
      updateUser(updatedUser)
      
      alert('銀行資料已儲存')
    } catch (error) {
      console.error('儲存銀行資料失敗:', error)
      alert(error instanceof Error ? error.message : '儲存失敗，請稍後再試')
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (user && user.bankInfo) {
      setBankData({
        bankName: user.bankInfo.bankName,
        bankCode: (user.bankInfo as any).bankCode || '',
        branchName: user.bankInfo.branchName,
        branchCode: user.bankInfo.branchCode || '',
        accountNumber: user.bankInfo.accountNumber,
        accountHolder: user.bankInfo.accountHolder
      })
    } else {
      setBankData({
        bankName: '',
        bankCode: '',
        branchName: '',
        branchCode: '',
        accountNumber: '',
        accountHolder: ''
      })
    }
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-secondary-600 dark:text-secondary-400">載入中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* 銀行匯款資料區塊 */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-primary-800 mb-6">
            銀行匯款資料
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 第一行：銀行名稱 + 銀行代號 */}
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                銀行名稱 *
              </label>
              <input
                type="text"
                value={bankData.bankName}
                onChange={(e) => setBankData(prev => ({ ...prev, bankName: e.target.value }))}
                className="input-primary"
                placeholder="例：玉山銀行"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                銀行代號 *
              </label>
              <input
                type="text"
                value={bankData.bankCode}
                onChange={(e) => setBankData(prev => ({ ...prev, bankCode: e.target.value }))}
                className="input-primary"
                placeholder="例：808"
                pattern="[0-9]{3,4}"
                title="請輸入3-4位數字的銀行代號"
                required
              />
              
            </div>
            
            {/* 第二行：分行名稱 + 分行代號 */}
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                分行名稱 *
              </label>
              <input
                type="text"
                value={bankData.branchName}
                onChange={(e) => setBankData(prev => ({ ...prev, branchName: e.target.value }))}
                className="input-primary"
                placeholder="例：台北分行"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                分行代號 *
              </label>
              <input
                type="text"
                value={bankData.branchCode}
                onChange={(e) => setBankData(prev => ({ ...prev, branchCode: e.target.value }))}
                className="input-primary"
                placeholder="例：0001"
                pattern="[0-9]{3,4}"
                title="請輸入3-4位數字的分行代號"
                required
              />
              <p className="text-xs text-secondary-500 mt-1">通常為帳戶號碼前4碼</p>
            </div>
            
            {/* 第三行：帳戶號碼 */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-primary-700 mb-2">
                帳戶號碼 *
              </label>
              <input
                type="text"
                value={bankData.accountNumber}
                onChange={(e) => setBankData(prev => ({ ...prev, accountNumber: e.target.value }))}
                className="input-primary"
                placeholder="請輸入完整帳戶號碼"
                required
              />
            </div>
            
            {/* 第四行：戶名 */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-primary-700 mb-2">
                戶名 *
              </label>
              <input
                type="text"
                value={bankData.accountHolder}
                onChange={(e) => setBankData(prev => ({ ...prev, accountHolder: e.target.value }))}
                className="input-primary"
                placeholder="請輸入帳戶戶名"
                required
              />
            </div>
          </div>

          {/* 資料顯示範例 */}
          {bankData.bankName && bankData.bankCode && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-sm font-medium text-blue-800 mb-2">資料預覽：</h4>
              <p className="text-sm text-blue-700">
                銀行名稱：{bankData.bankName} &nbsp;&nbsp;&nbsp;&nbsp; 銀行代號：{bankData.bankCode}
              </p>
              {bankData.branchName && bankData.branchCode && (
                <p className="text-sm text-blue-700">
                  分行名稱：{bankData.branchName} &nbsp;&nbsp;&nbsp;&nbsp; 分行代號：{bankData.branchCode}
                </p>
              )}
            </div>
          )}
        </div>

        {/* 操作按鈕 */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleReset}
            disabled={isLoading}
            className="btn-secondary"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary"
          >
            {isLoading ? (
              <>
                <div className="loading-spinner mr-2"></div>
                儲存中...
              </>
            ) : (
              '儲存變更'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default BankSettingsTab
